import programApi from "../api/program-api";

// The programs endpoints return ProgramBean / ProgramBean[] directly, but the rest of
// the app wraps payloads in the SUCCESS envelope ({ status, bean, list }). Normalize
// both shapes so the page code stays simple.
const normalizeList = (resp) => {
    if (!resp) {
        return [];
    }
    if (Array.isArray(resp)) {
        return resp;
    }
    const raw = resp.data || resp.list || resp.programs || resp.content;
    return Array.isArray(raw) ? raw : [];
};

const normalizeBean = (resp) => {
    if (!resp) {
        return null;
    }
    if (resp.bean) {
        return resp.bean;
    }
    if (resp.data && !Array.isArray(resp.data)) {
        return resp.data;
    }
    return resp;
};

/**
 * NMU-453: only treat `status` as an envelope result when it actually is one.
 *
 * These endpoints answer with a bare ProgramBean, and ProgramBean.status is the
 * published flag — a boolean. The old check read that boolean as an envelope
 * status, found `true !== "SUCCESS"`, and threw. Worse, a ProgramBean carries
 * none of responseMsgTh/responseMsgEn/message, so what it threw was `undefined`:
 * every Edit click on an Online programme (i.e. all of them) failed with
 * "unknow error undefined" and the dialog never opened.
 *
 * A real envelope always carries a string status, so that is what is checked,
 * and the throw always has something to say.
 */
const assertSuccess = (resp) => {
    if (resp && typeof resp.status === "string" && resp.status !== "SUCCESS") {
        throw resp.responseMsgTh || resp.responseMsgEn || resp.message || resp.status;
    }
    return resp;
};

// condition: { q, level, faculty }
const search = async (condition) => {
    try {
        const resp = await programApi.search(condition);
        return normalizeList(resp);
    } catch (err) {
        console.error(err);
        throw err;
    }
};

const getById = async (id) => {
    const resp = await programApi.getById(id);
    assertSuccess(resp);
    return normalizeBean(resp);
};

const add = async (bean) => {
    const resp = await programApi.add(bean);
    return assertSuccess(resp);
};

const edit = async (id, bean) => {
    const resp = await programApi.edit(id, bean);
    return assertSuccess(resp);
};

const remove = async (id) => {
    const resp = await programApi.remove(id);
    return assertSuccess(resp);
};

const programService = {
    search,
    getById,
    add,
    edit,
    remove,
    normalizeList,
    normalizeBean,
};

export default programService;
